import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Gift, Loader, ShieldCheck, Sparkles, ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BRAND, ZORBA } from "../data/content";
import { breadcrumbSchema, multiGraph } from "../components/Schema";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function GiftVouchers() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [form, setForm] = useState({
    recipient_name: "",
    recipient_mobile: "",
    recipient_email: "",
    sender_name: "",
    sender_contact: "",
    personal_message: "",
    design: "luxury",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/giftable-services`)
      .then((r) => {
        setServices(r.data.services || []);
        if (r.data.services?.[0]) setSelectedSlug(r.data.services[0].slug);
      })
      .catch(() => setError("Could not load giftable services. Please refresh and try again."));
  }, []);

  const selectedService = useMemo(
    () => services.find((s) => s.slug === selectedSlug),
    [services, selectedSlug],
  );

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function handlePurchase(e) {
    e.preventDefault();
    setError("");
    if (!selectedService) {
      setError("Please pick a voucher to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, service_slug: selectedService.slug };
      const { data } = await axios.post(`${API}/vouchers/create-order`, payload);

      if (!data.payment_configured) {
        // Keys not configured — show success path without charging. Voucher is pending.
        navigate(
          `/gift-vouchers/success/?code=${encodeURIComponent(data.voucher_code)}&pending=1`,
          { replace: false },
        );
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) {
        setError("Could not reach the payment provider. Please try again.");
        setSubmitting(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: ZORBA.name,
        description: `Gift voucher — ${selectedService.name}`,
        order_id: data.razorpay_order_id,
        prefill: {
          name: form.sender_name,
          contact: form.sender_contact,
        },
        notes: {
          voucher_code: data.voucher_code,
        },
        theme: { color: "#2D6A4F" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/vouchers/verify`, {
              voucher_id: data.voucher_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            navigate(`/gift-vouchers/success/?code=${encodeURIComponent(data.voucher_code)}`);
          } catch (err) {
            setError(
              err?.response?.data?.detail ||
                "Payment succeeded but we couldn't confirm it. Please message us on WhatsApp with your payment ID.",
            );
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }

  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Gift vouchers" },
  ];

  return (
    <>
      <Seo
        title="Gift vouchers at Zorba International Spa"
        description="Give the gift of wellness. Choose a Zorba Signature Therapy or a Couples suite experience and share a beautifully designed voucher with someone you love."
        path="/gift-vouchers/"
        jsonLd={multiGraph(breadcrumbSchema(crumbs))}
      />
      <section className="content-hero section-pad narrow">
        <Breadcrumbs items={crumbs} />
        <p className="eyebrow"><Gift size={13} /> {BRAND.short}</p>
        <h1>Gift a wellness ritual.</h1>
        <p className="large-copy">
          A thoughtful alternative to flowers or chocolate. Choose a Zorba Signature Therapy or a couples
          suite experience and we'll send you a beautifully designed voucher the recipient can redeem at
          Zorba International Spa, Chandrasekharpur.
        </p>
      </section>

      <section className="section-pad narrow">
        <h2 className="section-h">1 · Pick a voucher</h2>
        <div className="voucher-grid">
          {services.map((s, i) => {
            const active = s.slug === selectedSlug;
            return (
              <button
                type="button"
                key={s.slug}
                onClick={() => setSelectedSlug(s.slug)}
                className={`voucher-pick${active ? " active" : ""}`}
                data-testid={`voucher-pick-${i}`}
              >
                <div className="voucher-pick-head">
                  <span className="card-kicker">{s.category}</span>
                  {active && <Check size={16} className="voucher-check" />}
                </div>
                <h3>{s.name}</h3>
                <p className="muted-small">{s.description}</p>
                <strong className="voucher-pick-price">
                  ₹{s.price.toLocaleString("en-IN")}
                  <small>· {s.duration}</small>
                </strong>
              </button>
            );
          })}
        </div>

        <form onSubmit={handlePurchase} className="voucher-form" data-testid="voucher-form">
          <h2 className="section-h">2 · Who is this for?</h2>
          <div className="form-grid">
            <label>
              Recipient name
              <input
                required
                value={form.recipient_name}
                onChange={update("recipient_name")}
                placeholder="Full name"
                data-testid="voucher-recipient-name"
              />
            </label>
            <label>
              Recipient mobile
              <input
                required
                value={form.recipient_mobile}
                onChange={update("recipient_mobile")}
                placeholder="+91…"
                data-testid="voucher-recipient-mobile"
              />
            </label>
            <label>
              Recipient email (optional)
              <input
                type="email"
                value={form.recipient_email}
                onChange={update("recipient_email")}
                placeholder="you@example.com"
                data-testid="voucher-recipient-email"
              />
            </label>
            <label>
              Personal message
              <textarea
                rows={3}
                maxLength={500}
                value={form.personal_message}
                onChange={update("personal_message")}
                placeholder="A line or two they'll read on the voucher…"
                data-testid="voucher-personal-message"
              />
            </label>
          </div>

          <h2 className="section-h">3 · Your details</h2>
          <div className="form-grid">
            <label>
              Your name
              <input
                required
                value={form.sender_name}
                onChange={update("sender_name")}
                data-testid="voucher-sender-name"
              />
            </label>
            <label>
              Your mobile or email
              <input
                required
                value={form.sender_contact}
                onChange={update("sender_contact")}
                placeholder="So we can confirm payment"
                data-testid="voucher-sender-contact"
              />
            </label>
          </div>

          <input
            type="text"
            name="website"
            value={form.website}
            onChange={update("website")}
            tabIndex={-1}
            autoComplete="off"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          {error && <p className="form-error" data-testid="voucher-error">{error}</p>}

          <div className="voucher-summary">
            <div>
              <span className="muted-small">Selected</span>
              <strong>{selectedService?.name || "—"}</strong>
            </div>
            <div>
              <span className="muted-small">Amount</span>
              <strong>
                {selectedService ? `₹${selectedService.price.toLocaleString("en-IN")}` : "—"}
              </strong>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !selectedService}
              data-testid="voucher-submit"
            >
              {submitting ? (
                <>
                  <Loader size={14} className="spin" /> Opening checkout…
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Continue to payment <ArrowRight size={14} />
                </>
              )}
            </button>
          </div>
          <p className="muted-small flex-mini">
            <ShieldCheck size={13} /> Secure checkout via Razorpay. Voucher is emailed and ready to share instantly.
          </p>
        </form>
      </section>

      <section className="section-pad narrow">
        <div className="info-strip">
          <div>
            <strong>Instant delivery.</strong>
            <span className="muted-small">The voucher code lands in your success screen the moment payment clears.</span>
          </div>
          <div>
            <strong>6 months to redeem.</strong>
            <span className="muted-small">Plenty of time for the recipient to pick a date that fits their calendar.</span>
          </div>
          <div>
            <strong>Honour at Zorba.</strong>
            <span className="muted-small">Redeemed in person at the Chandrasekharpur spa — bring the voucher code.</span>
          </div>
        </div>
        <p className="muted-small" style={{ marginTop: 18 }}>
          Questions? <Link to="/contact/">Contact us</Link> or WhatsApp us before you buy.
        </p>
      </section>
    </>
  );
}
